import * as React from 'react'
import { Link } from 'react-router'
import { CheckCircle2, Circle, X } from 'lucide-react'
import type { GoLiveItemId } from '@lib/dto/tenant-settings.dto'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip } from '@/components/ui/tooltip'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { useGoLiveChecklist } from '@/hooks/useTenantSettings'
import { cn } from '@/lib/utils'

interface ItemCopy {
  title: string
  hint: string
  /** Under the dashboard's base path, or absolute for public pages. */
  to: string
  action: string
}

const COPY: Record<GoLiveItemId, ItemCopy> = {
  test_key: { title: 'Create a test key', hint: 'Build your integration against test mode first.', to: 'api-keys', action: 'API keys' },
  test_webhook_verified: {
    title: 'Verify your test webhook endpoint',
    hint: 'Set a test webhook URL, then send a test event until your endpoint answers with a 2xx.',
    to: 'webhooks',
    action: 'Webhooks',
  },
  test_envelope_finished: {
    title: 'Take a test envelope to the end',
    hint: 'Complete, decline, or void one, e.g. with the simulation endpoints.',
    to: '/docs',
    action: 'API docs',
  },
  live_webhook_url: { title: 'Set your live webhook URL', hint: 'Where live events go.', to: 'settings', action: 'Settings' },
  embed_origins: {
    title: 'Set embed origins',
    hint: 'Only if you embed signing in your app.',
    to: 'settings',
    action: 'Settings',
  },
  live_key: { title: 'Create a live key', hint: 'An owner creates live keys.', to: 'api-keys', action: 'API keys' },
}

function dismissKey(tenantId: string): string {
  return `pixelsign.goLiveDismissed.${tenantId}`
}

function readDismissed(tenantId: string): boolean {
  try {
    return window.localStorage.getItem(dismissKey(tenantId)) === '1'
  } catch {
    return false
  }
}

/**
 * "Get ready for live": the steps from a test integration to live traffic,
 * checked from the organization's own data. Advisory only (live keys are never
 * gated on it); shown to owners and developers until done or dismissed.
 */
export function GoLiveChecklist() {
  const scope = useDashboardScope()
  const canAct = !scope.readOnly && scope.role !== 'viewer'
  const checklist = useGoLiveChecklist({ enabled: canAct })
  const [dismissed, setDismissed] = React.useState(() => readDismissed(scope.tenantId))

  function dismiss(): void {
    setDismissed(true)
    try {
      window.localStorage.setItem(dismissKey(scope.tenantId), '1')
    } catch {
      // Private mode: it stays dismissed until the page reloads.
    }
  }

  if (!canAct || dismissed || !checklist.data || checklist.data.ready) return null
  const required = checklist.data.items.filter((item) => !item.optional)
  const doneCount = required.filter((item) => item.done).length

  return (
    <Card className="mb-6">
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle className="text-title">Get ready for live</CardTitle>
          <p className="text-caption text-muted-foreground">
            {doneCount} of {required.length} done. Nothing here blocks live keys.
          </p>
        </div>
        <Tooltip content="Hide checklist">
          <Button variant="ghost" size="icon" aria-label="Hide checklist" onClick={dismiss}>
            <X />
          </Button>
        </Tooltip>
      </CardHeader>
      <ul className="divide-y divide-border border-t border-border">
        {checklist.data.items.map((item) => {
          const copy = COPY[item.id]
          const to = copy.to.startsWith('/') ? copy.to : `${scope.basePath}/${copy.to}`
          return (
            <li key={item.id} className="flex items-start gap-3 px-card py-3">
              {item.done ? (
                <CheckCircle2 aria-label="Done" className="mt-0.5 size-4 shrink-0 text-success" />
              ) : (
                <Circle aria-label="Not done" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <p className={cn('text-body font-medium', item.done && 'text-muted-foreground')}>
                  {copy.title}
                  {item.optional ? <span className="ml-2 text-caption font-normal text-muted-foreground">Optional</span> : null}
                </p>
                {item.done ? null : <p className="text-caption text-muted-foreground">{copy.hint}</p>}
              </div>
              {item.done ? null : (
                <Link to={to} className="shrink-0 text-caption font-medium text-link">
                  {copy.action}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
